import adminIcon from '@fortawesome/fontawesome-pro/svgs/solid/hat-wizard.svg';
import instructorIcon from '@fortawesome/fontawesome-pro/svgs/solid/chalkboard-user.svg';
import reviewerIcon from '@fortawesome/fontawesome-pro/svgs/solid/user-pen.svg';
import participantIcon from '@fortawesome/fontawesome-pro/svgs/regular/user.svg';
import participantTriggerIcon from '@fortawesome/fontawesome-pro/svgs/regular/circle-user.svg';
import { USER_ROLES } from 'go-modules/services/auth/auth.service';

export interface Bindings {
	users: any[];
}

export interface ParticipantRole {
	icon: string;
	labelKey: string;
	name: 'admin' | 'owner' | 'reviewer' | 'participant';
	section: 'facilitators' | 'participants';
}

export interface ParticipantRoleUser {
	role: ParticipantRole;
	user: any;
}

const participantRoleIcons = {
	admin: adminIcon,
	owner: instructorIcon,
	reviewer: reviewerIcon,
	participant: participantIcon
} as const;

export class PresenterListController implements Bindings {
	public users: any[] = [];
	public isOpen = false;
	public facilitators: ParticipantRoleUser[] = [];
	public participants: ParticipantRoleUser[] = [];
	public participantTriggerIcon = participantTriggerIcon;
	private roleTooltipElement: HTMLElement | null = null;
	private triggerTooltipElement: HTMLElement | null = null;

	/* @ngInject */
	constructor (
		private $element: ng.IAugmentedJQuery,
		private $timeout: ng.ITimeoutService,
		private $document: ng.IDocumentService,
		private $translate: angular.translate.ITranslateService
	) {}

	public $onChanges (): void {
		const roleUsers = this.users
			.map((user) => ({ user, role: this.getParticipantRole(user) }))
			.sort((a, b) => this.getUserFullName(a.user).localeCompare(this.getUserFullName(b.user)));

		this.facilitators = roleUsers.filter(({ role }) => role.section === 'facilitators');
		this.participants = roleUsers.filter(({ role }) => role.section === 'participants');
	}

	public $onDestroy (): void {
		this.hideRoleTooltip();
		this.hideTriggerTooltip();
	}

	public togglePopover (): void {
		this.hideTriggerTooltip();
		this.isOpen = !this.isOpen;
	}

	public showRoleTooltip (event: FocusEvent | MouseEvent, labelKey: string): void {
		const target = event.currentTarget as HTMLElement;

		this.hideRoleTooltip();
		this.roleTooltipElement = this.$document[0].createElement('div');
		this.roleTooltipElement.className = 'participant-role-tooltip';
		this.roleTooltipElement.textContent = this.$translate.instant(labelKey);
		this.roleTooltipElement.style.visibility = 'hidden';
		this.$document[0].body.appendChild(this.roleTooltipElement);

		const targetRect = target.getBoundingClientRect();
		const tooltipRect = this.roleTooltipElement.getBoundingClientRect();
		const left = targetRect.left - tooltipRect.width - 10;
		const fallbackLeft = targetRect.right + 10;

		this.roleTooltipElement.style.left = `${Math.max(8, left >= 8 ? left : fallbackLeft)}px`;
		this.roleTooltipElement.style.top = `${targetRect.top + (targetRect.height / 2)}px`;
		this.roleTooltipElement.style.visibility = 'visible';
	}

	public showTriggerTooltip (event: FocusEvent | MouseEvent): void {
		const target = event.currentTarget as HTMLElement;

		this.hideTriggerTooltip();
		this.triggerTooltipElement = this.$document[0].createElement('div');
		this.triggerTooltipElement.className = 'participant-role-tooltip participant-trigger-tooltip';
		this.triggerTooltipElement.textContent = this.$translate.instant('participant-control_tooltip');
		this.triggerTooltipElement.style.visibility = 'hidden';
		this.$document[0].body.appendChild(this.triggerTooltipElement);

		const targetRect = target.getBoundingClientRect();
		const tooltipRect = this.triggerTooltipElement.getBoundingClientRect();
		const left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
		const maxLeft = this.$document[0].documentElement.clientWidth - tooltipRect.width - 8;

		this.triggerTooltipElement.style.left = `${Math.min(maxLeft, Math.max(8, left))}px`;
		this.triggerTooltipElement.style.top = `${targetRect.bottom + 10}px`;
		this.triggerTooltipElement.style.visibility = 'visible';
	}

	public hideTriggerTooltip (): void {
		this.triggerTooltipElement?.remove();
		this.triggerTooltipElement = null;
	}

	public hideRoleTooltip (): void {
		this.roleTooltipElement?.remove();
		this.roleTooltipElement = null;
	}

	public closeAndFocusTrigger (event?: KeyboardEvent): void {
		if (event != null && event.key !== 'Escape') {
			return;
		}

		if (event != null) {
			event.stopPropagation();
			event.preventDefault();
		}

		this.hideRoleTooltip();
		this.hideTriggerTooltip();
		this.isOpen = false;
		this.$timeout(() => {
			this.$element[0].querySelector<HTMLButtonElement>('.participant-trigger')?.focus();
		});
	}

	public getParticipantCount (): number {
		return this.users.length;
	}

	public getUserFullName (user: any): string {
		return `${user.first_name} ${user.last_name}`;
	}

	private getParticipantRole (user: any): ParticipantRole {
		switch (user.role) {
			case USER_ROLES.OWNER:
			case USER_ROLES.ADMIN:
				return {
					icon: participantRoleIcons.admin,
					labelKey: 'participant-role_admin',
					name: 'admin',
					section: 'facilitators'
				};
			case USER_ROLES.INSTRUCTOR:
				return {
					icon: participantRoleIcons.owner,
					labelKey: 'participant-role_owner',
					name: 'owner',
					section: 'facilitators'
				};
			case USER_ROLES.REVIEWER:
				return {
					icon: participantRoleIcons.reviewer,
					labelKey: 'participant-role_reviewer',
					name: 'reviewer',
					section: 'facilitators'
				};
			case USER_ROLES.PRESENTER:
			case null:
			default:
				return {
					icon: participantRoleIcons.participant,
					labelKey: 'participant-role_participant',
					name: 'participant',
					section: 'participants'
				};
		}
	}
}
